import { Directive, ElementRef, effect, inject, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { oneOfValidator } from '@shared/forms/forms.validators';
import { trackById } from '@shared/utils/filters';
import { Maybe, asOptional } from '@stillmandigital/common';
import { isString } from 'lodash';
import { Subscription, combineLatest, fromEvent, map, merge, startWith } from 'rxjs';

@Directive()
export abstract class AutocompleteSelectorComponent<T> implements ControlValueAccessor, Validator {
  private _fb = inject(FormBuilder);
  private _elementRef = inject(ElementRef);
  private _scrollSubscription: Subscription | undefined = undefined;

  public abstract matchOption(option: T, text: string): boolean;

  public abstract displayOption(option: T | string): string;

  public abstract extractId(option: Maybe<T>): string | number | undefined;

  public options = input<T[] | null>();

  public options$ = toObservable(this.options);

  public readOnly = input<boolean>(false);

  public readonly required = input(false);

  public autocompleteControl = this._fb.control(asOptional<T>(), oneOfValidator(this.options));

  public readonly filteredOptions$ = combineLatest([
    this.options$,
    this.autocompleteControl.valueChanges.pipe(startWith('')),
  ]).pipe(map(([options, value]) => options?.filter(o => (isString(value) ? this.matchOption(o, value) : options))));

  constructor() {
    effect(() => {
      if (this.required()) {
        this.autocompleteControl.addValidators(Validators.required);
      }
    });
  }

  public validate(_control: AbstractControl): ValidationErrors | null {
    if (this.readOnly()) {
      return null;
    }
    return this.autocompleteControl.errors;
  }

  public clear(event: MouseEvent) {
    event.stopPropagation();
    if (this.readOnly()) {
      return;
    }

    this.autocompleteControl.reset();
  }

  public onAutocompleteOpened(trigger: MatAutocompleteTrigger): void {
    const ancestors = this._getScrollableAncestors(this._elementRef.nativeElement);
    if (ancestors.length === 0) {
      return;
    }
    this._scrollSubscription = merge(...ancestors.map(el => fromEvent(el, 'scroll'))).subscribe(() =>
      trigger.closePanel(),
    );
  }

  public onAutocompleteClosed(): void {
    this._scrollSubscription?.unsubscribe();
    this._scrollSubscription = undefined;
  }

  private _getScrollableAncestors(element: HTMLElement): HTMLElement[] {
    const isScrollable = (el: HTMLElement): boolean => {
      const { overflow, overflowY } = getComputedStyle(el);
      return ['auto', 'scroll'].includes(overflow) || ['auto', 'scroll'].includes(overflowY);
    };

    const ancestors: HTMLElement[] = [];
    for (let el = element.parentElement; el; el = el.parentElement) {
      if (isScrollable(el)) {
        ancestors.push(el);
      }
    }
    return ancestors;
  }

  public readonly trackById = trackById;

  public writeValue(value: Maybe<T>): void {
    if (this.extractId(this.autocompleteControl.value) !== this.extractId(value)) {
      this.autocompleteControl.setValue(value, { emitEvent: false });
      this.autocompleteControl.setErrors(null, { emitEvent: false });
    }
  }

  public registerOnChange(fn: (value: Maybe<T>) => void): void {
    this.autocompleteControl.valueChanges.subscribe(fn);
  }

  public registerOnTouched(fn: () => void): void {
    this.autocompleteControl.valueChanges.subscribe(fn);
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.autocompleteControl.disable();
    } else {
      this.autocompleteControl.enable();
    }
  }
}
